import type { Element, Properties, Root } from "hast";
import type { Plugin } from "unified";
import { visit } from "unist-util-visit";

/**
 * Transient provenance marker shared by the v2 MDAST→HAST handlers and the
 * term-highlight HAST pass (§6.3).
 *
 * `mdast-util-to-hast` handlers tag the elements they emit for MDAST-originated
 * `code`/`inlineCode`, parsed-Markdown links, and citation anchors with this
 * marker. The term pass then skips only those subtrees, while still
 * highlighting text inside raw-HTML elements materialised by `rehypeRaw` (which
 * never carry the marker) — matching v1's tags-only protection. The marker is
 * stripped from the tree before render so it never reaches the DOM.
 */
export const MDAST_ORIGIN_PROPERTY = "dataMdastOrigin";

/** Marker value written to {@link MDAST_ORIGIN_PROPERTY}. */
export const MDAST_ORIGIN_VALUE = "1";

/** Sets the provenance marker on a HAST element's properties. */
export function withMdastOrigin(properties: Properties): Properties {
  return { ...properties, [MDAST_ORIGIN_PROPERTY]: MDAST_ORIGIN_VALUE };
}

/** True when an element was tagged as MDAST-originated. */
export function hasMdastOrigin(element: Element): boolean {
  return element.properties[MDAST_ORIGIN_PROPERTY] === MDAST_ORIGIN_VALUE;
}

/**
 * Rehype pass removing the transient provenance marker so it never appears in
 * the rendered DOM. Runs last, after the term pass has consumed it.
 */
export const rehypeStripMdastOrigin: Plugin<[], Root> = function () {
  return function (tree: Root): void {
    visit(tree, "element", (element: Element) => {
      if (MDAST_ORIGIN_PROPERTY in element.properties) {
        delete element.properties[MDAST_ORIGIN_PROPERTY];
      }
    });
  };
};
